import React, { memo } from "react";
import { InfoCircleFilled } from "@ant-design/icons";
import { Tooltip } from "antd";
import styles from "./FieldWrapper.module.scss";

export interface FieldWrapperProps {
  /** Field label */
  label?: string;

  /** Field name/id */
  name: string;

  /** Error message if validation failed */
  error?: string;

  /** Tooltip/help text */
  tooltip?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Custom class for the wrapper */
  className?: string;

  /** Whether the field is hidden */
  hidden?: boolean;

  /** Children (the actual input component) */
  children: React.ReactNode;
}

/**
 * FieldWrapper - Common wrapper for all form fields
 * Handles labels, error display, tooltips, and consistent layout
 */
export const FieldWrapper: React.FC<FieldWrapperProps> = memo(
  ({ label, name, error, tooltip, required, className, hidden, children }) => {
    if (hidden) {
      return <>{children}</>;
    }

    const errorId = error ? `${name}-error` : undefined;

    return (
      <div className={`${styles.fieldWrapper} ${className || ""}`}>
        <div className={styles.fieldContainer}>
          {label && (
            <div className={styles.labelContainer}>
              <label htmlFor={name} className={styles.label}>
                {label}
                {required && (
                  <span className={styles.required} aria-hidden="true">
                    *
                  </span>
                )}
              </label>
              {tooltip && (
                <Tooltip title={tooltip}>
                  <InfoCircleFilled
                    className={styles.tooltipIcon}
                    aria-label={`Help: ${tooltip}`}
                  />
                </Tooltip>
              )}
            </div>
          )}
          <div className={styles.inputContainer}>{children}</div>
          {error && (
            <div
              id={errorId}
              className={styles.errorContainer}
              role="alert"
              aria-live="polite"
            >
              <span className={styles.errorMessage}>{error}</span>
            </div>
          )}
        </div>
      </div>
    );
  },
);

FieldWrapper.displayName = "FieldWrapper";
